import { useNavigate } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import type { ApprovalItem } from '@/utils/dashboard';

const typeIcon: Record<string, string> = {
  Invoice: 'ri-file-text-line',
  'Payment batch': 'ri-stack-line',
  Content: 'ri-movie-line',
  Contract: 'ri-file-paper-2-line',
};

interface DueTodayApprovalsModalProps {
  open: boolean;
  items: ApprovalItem[];
  onClose: () => void;
}

export default function DueTodayApprovalsModal({
  open,
  items,
  onClose,
}: DueTodayApprovalsModalProps) {
  const navigate = useNavigate();

  const openQueue = () => {
    onClose();
    navigate('/invoices');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-alarm-warning-line"
      title="Approvals due today"
      subtitle={`${items.length} item${items.length === 1 ? '' : 's'} across invoices, batches, content and contracts`}
      widthClassName="max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button icon="ri-arrow-right-line" onClick={openQueue}>
            Open approval queue
          </Button>
        </>
      }
    >
      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-foreground-500">
          Nothing is due today. You are all caught up.
        </p>
      ) : (
        <ul className="divide-y divide-background-100 rounded-md border border-background-200">
          {items.map((item) => (
            <li key={item.id} className="flex items-start gap-3 px-3 py-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-base">
                <i className={typeIcon[item.type] ?? 'ri-checkbox-circle-line'} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                  {item.type}
                </span>
                <p className="mt-0.5 text-sm font-medium text-foreground-950">
                  {item.title}
                </p>
                <p className="text-xs text-foreground-500">{item.detail}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <span className="text-sm font-medium text-foreground-900">
                  {item.amount}
                </span>
                <StatusBadge status={item.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}